import * as yup from 'yup'

import { Button, Form } from 'react-bootstrap'
import { ButtonContainer, XStack, YStack } from '../index.style'
import React, { useRef } from 'react'
import { arrayOfIntegers, getImageWidth, getSummaryHTML } from '../utils'

import CheckboxField from './FormFields/CheckboxField'
import DateTimeField from './FormFields/DateTimeField'
import { Formik } from 'formik'
import NumberField from './FormFields/NumberField'
import Swal from 'sweetalert2'
import TextField from './FormFields/TextField'
import UploadImageField from './FormFields/UploadImageField/UploadImageField'
import moment from 'moment'

const FormHighlightLottery = ({ handleSubmitForm, isLoading, defaultValues }) => {
  const formRef = useRef()

  const info = defaultValues?.payload ? JSON.parse(defaultValues.payload) : null

  const schema = yup.object().shape({
    title: yup.string().required('กรุณากรอกหัวข้อไฮไลท์ลอตเตอรี่'),
    describe: yup.string(),
    size: yup
      .string()
      .matches(/^\d+(,\d+)*$/, 'ขนาดต้องมีเฉพาะตัวเลขคั่นด้วยเครื่องหมาย ,(comma) เท่านั้น')
      .test('size-not-empty', 'ขนาดชุดต้องมากกว่า 0', (value) => {
        if (!value) return true
        const valuesArray = arrayOfIntegers(value)

        const found = valuesArray.some((r) => [0].includes(r))

        return !found
      })
      .test('max-size', 'จำนวนชุด ต้องไม่เกิน 5 ชุด', (value) => {
        if (!value) return true
        const valuesArray = arrayOfIntegers(value)
        return valuesArray.length <= 5
      })
      .required('ขนาดชุดต้องมากกว่า 1'),
    image: yup
      .mixed()
      .nullable()
      .when('title', ([title], schema) => {
        if (!title) {
          return schema.required('กรุณากรอกหัวข้อหรืออัปโหลดรูปภาพ')
        }
      })
      .test('is-valid-width', 'รูปภาพต้องกว้าง 1400px เท่านั้น', async (file) => {
        if (!file) return true
        const width = await getImageWidth(file)
        return width === 1400
      }),
    start: yup.date().required('กรุณากรอกเวลาที่เริ่มขาย'),
    end: yup
      .date()
      .required('กรุณากรอกเวลาที่หยุดขาย')
      .when('start', ([start], schema) => {
        if (start) {
          return schema.min(start, 'กรุณากรอกเวลาที่หยุดขายให้ถูกต้อง (ต้องมากกว่าเวลาที่เริ่มขาย)')
        }
      }),
    rangeTime: yup.number().required('กรุณากรอกระยะเวลา'),
    showCountdown: yup.boolean().required(),
  })

  const handleRangeTimeFieldOnChange = (e) => {
    if (e.target.value.length > 10) return
    const fieldStart = formRef.current.getFieldProps({ name: 'start' })
    const fieldRangeTime = formRef.current.getFieldProps({ name: 'rangeTime' })
    if (e.target.value >= 0) {
      fieldRangeTime.onChange(e)
      const increaseTime = moment(fieldStart.value).add(e.target.value, 'minutes').format('YYYY-MM-DDTHH:mm')
      formRef.current.setFieldValue('end', increaseTime)
    }
  }

  const handleEndFieldOnChange = (e) => {
    formRef.current.setFieldTouched('end', false, false)
    const fieldStart = formRef.current.getFieldProps({ name: 'start' })
    const fieldEnd = formRef.current.getFieldProps({ name: 'end' })

    fieldEnd.onChange(e)
    const diff = moment(e.target.value).diff(moment(fieldStart.value), 'minutes')
    if (diff > 0) {
      formRef.current.setFieldValue('rangeTime', diff)
    } else {
      formRef.current.setFieldValue('rangeTime', 0)
    }
  }

  const handleClickClearConfig = () => {
    Swal.fire({
      icon: 'info',
      title: 'ต้องการล้างการตั้งค่าหรือไม่',
      text: 'การตั้งค่าทั้งหมดจะถูกล้าง',
      showCancelButton: true,
      confirmButtonText: 'ตกลง',
      cancelButtonText: 'ยกเลิก',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          formRef.current.resetForm()
          Swal.fire({
            icon: 'success',
            title: 'ล้างการตั้งค่าสำเร็จ',
          })
        } catch (error) {
          Swal.fire({
            icon: 'error',
            title: 'เกิดข้อผิดพลาด',
            text: error.message,
          })
        }
      }
    })
  }

  const handleClickSubmit = (values) => {
    const html = getSummaryHTML({
      description: values.describe,
      end: values.end,
      headerImage: values.image,
      minutesToClose: values.rangeTime,
      showCountdown: values.showCountdown,
      size: values.size,
      start: values.start,
      title: values.title,
    })

    Swal.fire({
      icon: 'info',
      title: 'ต้องการบันทึกการตั้งค่าหรือไม่',
      html: html,
      showCancelButton: true,
      confirmButtonText: 'บันทึก',
      cancelButtonText: 'ยกเลิก',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          handleSubmitForm(values)
        } catch (error) {
          Swal.fire({
            icon: 'error',
            title: 'เกิดข้อผิดพลาด',
            text: error.message,
          })
        }
      }
    })
  }

  return (
    <Formik
      innerRef={(f) => (formRef.current = f)}
      enableReinitialize
      validationSchema={schema}
      onSubmit={() => handleClickSubmit(formRef.current.values)}
      initialValues={{
        title: '',
        describe: '',
        size: '',
        image: null,
        start: moment().format('YYYY-MM-DDTHH:mm'),
        end: moment().add(1, 'days').format('YYYY-MM-DDTHH:mm'),
        schedule: undefined,
        rangeTime: moment().add(1, 'days').diff(moment(), 'minutes'),
        showCountdown: false,
      }}
    >
      {({ handleSubmit, values }) => (
        <Form onSubmit={handleSubmit}>
          <YStack style={{ gap: 16 }}>
            <TextField
              label="ขนาดชุด"
              placeholder="กรอกขนาดชุด"
              name="size"
              helperText="เว้นด้วยลูกน้ำ (,) เช่น 2,5 หมายถึง ชุด 2ใบ และ 5ใบ"
            />
            <UploadImageField label="รูปภาพ" name="image" defaultUrl={info?.HeaderImage} />
            <TextField
              label="หัวข้อ"
              placeholder="กรอกหัวข้อ"
              name="title"
              helperText="หากมีรูปภาพ ข้อความส่วนนี้จะไม่แสดง"
            />
            <TextField
              label="คำอธิบาย"
              placeholder="กรอกคำอธิบาย"
              name="describe"
              helperText="กรอกคำอธิบายที่จะแสดงใต้รูปภาพหรือหัวข้อ ในหน้าเว็บไซต์"
            />

            <XStack style={{ gap: 16 }}>
              <DateTimeField label="เวลาที่เริ่มขาย" name="start" />
              <NumberField
                label="นาที"
                name="rangeTime"
                disabled={!values.start}
                onChange={handleRangeTimeFieldOnChange}
              />
              <DateTimeField
                label="เวลาที่หยุดขาย"
                name="end"
                disabled={!values.start}
                onChange={handleEndFieldOnChange}
              />
            </XStack>
            <CheckboxField label="แสดงเวลานับถอยหลัง" name="showCountdown" />
            <ButtonContainer>
              <Button variant="danger" onClick={handleClickClearConfig} disabled={isLoading}>
                ล้างการตั้งค่า
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Loading…' : 'บันทึก'}
              </Button>
            </ButtonContainer>
          </YStack>
        </Form>
      )}
    </Formik>
  )
}

export default FormHighlightLottery
